import { Injectable } from '@angular/core';
import { concat, forkJoin, map, Observable } from 'rxjs';
import { PostTracker, PutTracker, Tracker } from 'src/app/interfaces/api/tracker';
import { ApiService } from '../../core/api.service';
import { RequestService } from '../../core/request.service';
import { WebSocketService } from '../../core/web-socket.service';

type QueryParams = Record<string, string | number | boolean | Date | Array<string | number | boolean | Date> | object>;

@Injectable({
  providedIn: 'root'
})

/**
 * Handle requests for tracker collection
 */
export class TrackerService {

  collection = "Tracker";
  collectionPath = "trackers";
  trackers: Tracker[] = [];

  constructor(
    private request: RequestService,
    private socket: WebSocketService,
    private apiService: ApiService
  ) { }

  //---GET---//

  getTrackers(): Observable<any> {
    return concat(
      this.request.getCollection(this.collection, this.collectionPath, {}),
      this.socket.getUpdate(this.collection)
    )
  }

  getTrackersForDatabase(database: string): Observable<Tracker[]> {
    return this.apiService.getCollection(this.collectionPath, '', { database });
  }

  getTrackersByDatabases(databases: string[]) {
    return forkJoin(
      databases.map(database => this.getUserTrackers({ database })
        .pipe(map(trackers => ({ database, trackers })))
      )
    )
  }
  
  getTracker(id: string): Observable<any> {
    return this.request.getItem(this.collection, this.collectionPath, id)
  }

  getUserTrackers(query: QueryParams): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'user', query);
  }

  searchTrackers(search: string, query: QueryParams = {}): Observable<Tracker[]> {
    return this.apiService.getCollection(this.collectionPath, 'search', { q: search, ...query });
  }

  searchAndCountTrackers(search: string, query: QueryParams = {}): Observable<{ results: Tracker[], total: number }> {
    return this.apiService.getCollection(this.collectionPath, 'searchAndCount', { q: search, ...query });
  }

  getStats(trackerName: string, project: string): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'stats', { name: trackerName, project });
  }

  getTrackersCountByDatabase(): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'countByDatabase');
  }

  getTrackersCount(search: string = "", query: QueryParams = {}): Observable<number> {
    return this.apiService.getCollection(this.collectionPath, 'count', { q: search, ...query });
  }

  getTrackersLight(query: QueryParams = {}): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'light', query);
  }

  getMasterTracker(id: string): Observable<any> {
    return this.apiService.getCollectionItem(this.collectionPath, 'master', id);
  }

  getMasterTrackerByUuid(id: string): Observable<any> {
    return this.apiService.getCollectionItem(this.collectionPath, 'master/uuid', id);
  }

  // TrackerConfig templates (Devices > gestion des modèles) - concat with the same live-socket
  // pattern getTrackers() already uses: the initial HTTP fetch, then every subsequent create/
  // update/delete broadcast (see unifield-api-v3's POST /trackers/config and
  // DELETE /trackers/configs/:id) re-emits the whole up-to-date collection, so any component
  // subscribed to this (e.g. tracker-configs.component.ts) stays live with no extra wiring.
  getConfigs(): Observable<any> {
    return concat(
      this.request.getCollection('TrackerConfig', this.collectionPath, {}, 'configs'),
      this.socket.getUpdate('TrackerConfig')
    )
  }

  getLastUpdatesByDb(query: { databases: string[], trackerName?: string, uuids: string[] }): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'lastUpdatesByDb', query);
  }


  getStatus(trackers: QueryParams): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'status', { trackers });
  }

  // getConfig(trackerIds: string[]) {
  //   return this.http.get(`${this.apiUrl}/api/${this.collectionPath}/getConfig?trackers=${trackerIds.join(',')}`)
  // }

  //---POST---//

  addTracker(tracker: PostTracker | PutTracker): Observable<any> {
    return this.request.postItem(this.collection, this.collectionPath, tracker)
  }

  setConfig(config: QueryParams): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'config', config);
  }

  setProject(uuids: string[], projectId: string): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'setProject', { uuids, projectId });
  }

  setTrackersConfig(uuids: string[], config: string): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'setConfig', { uuids, config });
  }

  positionOfTracksAtTime(payload: QueryParams): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'getPositionOfTracksAtTime', payload);
  }

  //---PUT---//

  setTracker(tracker: PutTracker): Observable<any> {
    return this.request.setProjectData(this.collection, this.collectionPath, tracker)
  }

  mergeTrackers(payload: { toMerge: Tracker, toDelete: Tracker }): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, 'merge', payload);
  }

  //---DELETE---//

  deleteTracker(id: string): Observable<any> {
    return this.request.deleteItem(this.collection, this.collectionPath, id)
  }

  deleteTrackers(query: QueryParams): Observable<any> {
    return this.request.deleteManyItems(this.collection, this.collectionPath, query)
  }

  deleteConfig(configId: string): Observable<any> {
    return this.apiService.deleteCollectionItem(this.collectionPath + "/configs", configId);
  }
}
